import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import { generateGroupCompliancePDF } from '../../utils/pdf';
import {
  IconBuilding,
  IconClipboardCheck,
  IconAlertTriangle,
  IconTruck,
  IconDroplet,
  IconArrowRight,
  IconCarCrash,
  IconClock,
  IconFileTypePdf,
} from '@tabler/icons-react';

export default function GroupOverview() {
  const { userProfile, org, orgId } = useAuth();
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!orgId) return;
      try {
        const branchesSnap = await getDocs(collection(db, 'organisations', orgId, 'branches'));
        const branchList = branchesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        const enriched = await Promise.all(branchList.map(async (branch) => {
          const [assetsSnap, checksSnap, tanksSnap] = await Promise.all([
            getDocs(collection(db, 'organisations', orgId, 'branches', branch.id, 'assets')),
            getDocs(collection(db, 'organisations', orgId, 'branches', branch.id, 'checkRecords')),
            getDocs(collection(db, 'organisations', orgId, 'branches', branch.id, 'dervTanks')),
          ]);

          const assets = assetsSnap.docs.map(d => d.data());
          const checks = checksSnap.docs.map(d => d.data());
          const tanks = tanksSnap.docs.map(d => d.data());

          const blocked = assets.filter(a => a.isBlocked).length;
          const todayChecks = checks.filter(c => {
            if (!c.submittedAt) return false;
            const date = c.submittedAt.toDate ? c.submittedAt.toDate() : new Date(c.submittedAt);
            return date.toDateString() === new Date().toDateString();
          });
          const failedToday = todayChecks.filter(c => c.result === 'fail').length;

          const lowestTank = tanks.reduce((min, t) => {
            const pct = Math.round((t.currentLitres / t.capacityLitres) * 100);
            return pct < min ? pct : min;
          }, 100);

          const compliance = branch.complianceScore || 0;
          const status = blocked > 0 || failedToday > 0 || lowestTank <= 15
            ? 'danger'
            : compliance < 85 || lowestTank <= 30
              ? 'warn'
              : 'ok';

          return {
            ...branch,
            totalAssets: assets.length,
            blocked,
            checksToday: todayChecks.length,
            checksTotal: checks.length,
            checksPassed: checks.filter(c => c.result === 'pass').length,
            checksFailed: checks.filter(c => c.result === 'fail').length,
            failedToday,
            tankPct: lowestTank,
            compliance,
            complianceScore: compliance,
            incidents: 0,
            status,
          };
        }));

        setBranches(enriched);

        const incidentsSnap = await getDocs(
          query(
            collection(db, 'organisations', orgId, 'incidents'),
            orderBy('detectedAt', 'desc'),
            limit(5)
          )
        );
        const incidentsList = incidentsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setIncidents(incidentsList);

        // Enrich branches with incident counts
        setBranches(prev => prev.map(b => ({
          ...b,
          incidents: incidentsList.filter(i => i.branchId === b.id && i.status === 'open').length,
        })));
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [orgId]);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const dateStr = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const brandColour = org?.brandColour || 'var(--color-brand)';

  const totalChecks = branches.reduce((sum, b) => sum + (b.checksToday || 0), 0);
  const totalBlocked = branches.reduce((sum, b) => sum + (b.blocked || 0), 0);
  const totalFailed = branches.reduce((sum, b) => sum + (b.failedToday || 0), 0);

  const statusColour = (s) => s === 'danger' ? 'var(--color-danger)' : s === 'warn' ? 'var(--color-warning)' : 'var(--color-success)';
  const statusBg = (s) => s === 'danger' ? 'var(--color-danger-light)' : s === 'warn' ? 'var(--color-warning-light)' : 'var(--color-success-light)';
  const statusLabel = (s) => s === 'danger' ? 'Needs attention' : s === 'warn' ? 'Monitor' : 'All good';

  const formatTime = (ts) => {
    if (!ts) return '—';
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === today.toDateString()) {
      return `Today ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
    }
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';

  const avatarColours = [
    { bg: '#E6F1FB', color: '#185FA5' },
    { bg: '#EAF3DE', color: '#3B6D11' },
    { bg: '#EEEDFE', color: '#534AB7' },
    { bg: '#FCEBEB', color: '#A32D2D' },
    { bg: '#FDF0E8', color: '#993C1D' },
    { bg: '#E1F5EE', color: '#085041' },
  ];

  const handleExportPDF = async () => {
    setExportingPdf(true);
    try {
      const monthLabel = new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
      generateGroupCompliancePDF(branches, org, monthLabel);
    } catch (err) {
      console.error(err);
    }
    setExportingPdf(false);
  };

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', minHeight: '100vh' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 400, color: 'var(--color-text-primary)', marginBottom: 4 }}>
              {greeting}, {userProfile?.name?.split(' ')[0] || 'there'}
            </h1>
            <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>{dateStr}</p>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, background: brandColour + '18', color: brandColour, borderRadius: 'var(--radius-full)', padding: '3px 10px', marginTop: 8 }}>
              <IconBuilding size={12} stroke={1.5} />
              {org?.name || 'Group overview'} · All branches
            </div>
          </div>
          <button
            onClick={handleExportPDF}
            disabled={branches.length === 0 || exportingPdf}
            style={{ background: 'var(--color-brand)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '8px 16px', fontSize: 13, cursor: branches.length === 0 || exportingPdf ? 'not-allowed' : 'pointer', opacity: branches.length === 0 || exportingPdf ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <IconFileTypePdf size={15} stroke={1.5} /> {exportingPdf ? 'Generating...' : 'Group compliance PDF'}
          </button>
        </div>

        {/* Group stats */}
        {!loading && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 12, marginBottom: 20 }}>
            {[
              { label: 'Total checks today', value: totalChecks, colour: 'var(--color-success)', icon: IconClipboardCheck },
              { label: 'Failed checks today', value: totalFailed, colour: totalFailed > 0 ? 'var(--color-danger)' : 'var(--color-success)', icon: IconClock },
              { label: 'Active incidents', value: incidents.filter(i => i.status === 'open').length, colour: incidents.filter(i => i.status === 'open').length > 0 ? 'var(--color-danger)' : 'var(--color-success)', icon: IconCarCrash },
              { label: 'Assets blocked', value: totalBlocked, colour: totalBlocked > 0 ? 'var(--color-danger)' : 'var(--color-success)', icon: IconTruck },
            ].map(stat => {
              const Icon = stat.icon;
              return (
                <div key={stat.label} style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '18px 20px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                    <div style={{ fontSize: 36, fontWeight: 400, color: stat.colour, lineHeight: 1 }}>{stat.value}</div>
                    <div style={{ width: 32, height: 32, borderRadius: 'var(--radius-md)', background: stat.colour + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon size={16} color={stat.colour} stroke={1.5} />
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{stat.label}</div>
                </div>
              );
            })}
          </div>
        )}

        {/* Branch cards */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 400, color: 'var(--color-text-primary)', marginBottom: 12 }}>Branch overview</div>

          {loading ? (
            <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', padding: '32px 0', textAlign: 'center' }}>Loading branches...</div>
          ) : branches.length === 0 ? (
            <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '48px 32px', textAlign: 'center' }}>
              <IconBuilding size={32} color="var(--color-text-tertiary)" stroke={1.5} style={{ marginBottom: 12 }} />
              <div style={{ fontSize: 14, fontWeight: 400, color: 'var(--color-text-primary)', marginBottom: 6 }}>No branches yet</div>
              <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>Add branches in Settings to see your group overview</div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 12 }}>
              {branches.map((branch) => (
                <div
                  key={branch.id}
                  onClick={() => navigate('/dashboard')}
                  style={{ background: 'var(--color-bg-primary)', border: `0.5px solid ${branch.status === 'danger' ? 'var(--color-danger)' : 'var(--color-border-light)'}`, borderRadius: 'var(--radius-lg)', padding: '18px 20px', cursor: 'pointer', transition: 'border-color 0.15s' }}
                  onMouseEnter={e => branch.status !== 'danger' && (e.currentTarget.style.borderColor = 'var(--color-border-medium)')}
                  onMouseLeave={e => branch.status !== 'danger' && (e.currentTarget.style.borderColor = 'var(--color-border-light)')}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 32, height: 32, borderRadius: 'var(--radius-md)', background: brandColour + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <IconBuilding size={16} color={brandColour} stroke={1.5} />
                      </div>
                      <div style={{ fontSize: 15, fontWeight: 400, color: 'var(--color-text-primary)' }}>{branch.name}</div>
                    </div>
                    <div style={{ fontSize: 10, padding: '3px 10px', borderRadius: 20, background: statusBg(branch.status), color: statusColour(branch.status) }}>
                      {statusLabel(branch.status)}
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 8, marginBottom: 14 }}>
                    {[
                      { label: 'Checks', value: branch.checksToday, icon: IconClipboardCheck, colour: 'var(--color-success)' },
                      { label: 'Failed', value: branch.failedToday, icon: IconClock, colour: branch.failedToday > 0 ? 'var(--color-danger)' : 'var(--color-text-tertiary)' },
                      { label: 'Incidents', value: incidents.filter(i => i.branchId === branch.id && i.status === 'open').length, icon: IconAlertTriangle, colour: incidents.filter(i => i.branchId === branch.id && i.status === 'open').length > 0 ? 'var(--color-danger)' : 'var(--color-text-tertiary)' },
                      { label: 'Blocked', value: branch.blocked, icon: IconTruck, colour: branch.blocked > 0 ? 'var(--color-danger)' : 'var(--color-text-tertiary)' },
                    ].map(s => {
                      const SIcon = s.icon;
                      return (
                        <div key={s.label} style={{ background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)', padding: '10px 8px', textAlign: 'center' }}>
                          <div style={{ fontSize: 20, fontWeight: 400, color: s.colour, marginBottom: 2 }}>{s.value}</div>
                          <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>{s.label}</div>
                        </div>
                      );
                    })}
                  </div>

                  <div style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>Compliance</span>
                      <span style={{ fontSize: 11, fontWeight: 400, color: branch.compliance >= 90 ? 'var(--color-success)' : branch.compliance >= 75 ? 'var(--color-warning)' : 'var(--color-danger)' }}>
                        {branch.compliance}%
                      </span>
                    </div>
                    <div style={{ height: 4, background: 'var(--color-bg-secondary)', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${branch.compliance}%`, background: branch.compliance >= 90 ? 'var(--color-success)' : branch.compliance >= 75 ? 'var(--color-warning)' : 'var(--color-danger)', borderRadius: 2 }} />
                    </div>
                  </div>

                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <IconDroplet size={11} stroke={1.5} /> Derv tank
                      </span>
                      <span style={{ fontSize: 11, fontWeight: 400, color: branch.tankPct <= 15 ? 'var(--color-danger)' : branch.tankPct <= 30 ? 'var(--color-warning)' : 'var(--color-success)' }}>
                        {branch.tankPct === 100 ? '—' : `${branch.tankPct}%`}
                      </span>
                    </div>
                    {branch.tankPct < 100 && (
                      <div style={{ height: 4, background: 'var(--color-bg-secondary)', borderRadius: 2, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${branch.tankPct}%`, background: branch.tankPct <= 15 ? 'var(--color-danger)' : branch.tankPct <= 30 ? 'var(--color-warning)' : 'var(--color-success)', borderRadius: 2 }} />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent incidents */}
        <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '0.5px solid var(--color-border-light)' }}>
            <div style={{ fontSize: 14, fontWeight: 400, color: 'var(--color-text-primary)' }}>Recent incidents — all branches</div>
            <span onClick={() => navigate('/incidents')} style={{ fontSize: 12, color: brandColour, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
              View all <IconArrowRight size={13} stroke={1.5} />
            </span>
          </div>

          {incidents.length === 0 ? (
            <div style={{ padding: '32px', textAlign: 'center', fontSize: 13, color: 'var(--color-text-tertiary)' }}>
              No incidents recorded — great work
            </div>
          ) : incidents.map((inc, i) => {
            const colours = avatarColours[i % avatarColours.length];
            return (
              <div
                key={inc.id}
                onClick={() => navigate(`/incidents/${inc.id}`)}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', borderBottom: i < incidents.length - 1 ? '0.5px solid var(--color-border-light)' : 'none', cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bg-secondary)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: colours.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: colours.color, flexShrink: 0 }}>
                  {getInitials(inc.driverName)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 400, color: 'var(--color-text-primary)', marginBottom: 2 }}>{inc.driverName || 'Unknown driver'}</div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', display: 'flex', alignItems: 'center', gap: 5 }}>
                    {inc.assetReg && <span style={{ fontFamily: 'monospace', fontSize: 10, background: '#FAC775', color: '#412402', padding: '1px 5px', borderRadius: 3 }}>{inc.assetReg}</span>}
                    {inc.branchId}
                  </div>
                </div>
                {inc.gforce && <div style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--color-text-secondary)', flexShrink: 0 }}>{inc.gforce}G</div>}
                <div style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, background: inc.severity === 'high' ? 'var(--color-danger-light)' : 'var(--color-warning-light)', color: inc.severity === 'high' ? 'var(--color-danger)' : 'var(--color-warning)', flexShrink: 0 }}>
                  {inc.severity ? inc.severity.charAt(0).toUpperCase() + inc.severity.slice(1) : 'Unknown'}
                </div>
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', flexShrink: 0, whiteSpace: 'nowrap' }}>{formatTime(inc.detectedAt)}</div>
              </div>
            );
          })}
        </div>

      </div>
    </Layout>
  );
}